// src/components/News.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

const MOBILE_QUERY = "(max-width: 768px)";

// 類別 → 底色/邊框/主色（與 admin/notice 相同）
function catStyle(cat) {
  switch (cat) {
    case "重要資訊":
      return { bg: "#fee2e2", bd: "#fecaca", fg: "#991b1b" }; // 淡紅
    case "衛教新知":
      return { bg: "#fef9c3", bd: "#fde68a", fg: "#92400e" }; // 淡黃
    case "醫療服務":
      return { bg: "#e0f2fe", bd: "#bae6fd", fg: "#0c4a6e" }; // 淡藍
    case "人才招募":
      return { bg: "#ede9fe", bd: "#ddd6fe", fg: "#5b21b6" }; // 淡紫
    case "活動相關":
      return { bg: "#dcfce7", bd: "#bbf7d0", fg: "#166534" }; // 淡綠
    default:
      return { bg: "#f1f5f9", bd: "#e2e8f0", fg: "#334155" };
  }
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}/${m}/${da}`;
  } catch {
    return iso;
  }
}

export default function News() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const hoveredRef = useRef(false);
  const [loading, setLoading] = useState(true); // ★ 修改：新增載入狀態
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );

  // 監聽螢幕尺寸，動態切換手機/桌機
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener?.("change", handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  // 拉最新公告（僅顯示 is_active）
  useEffect(() => {
    let mounted = true;
    supabase
      .from("notices")
      .select("id, title, category, published_at, is_active")
      .eq("is_active", true)
      .order("published_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (!mounted) return;
        if (error) {
          console.error("[News] fetch notices error:", error);
          setItems([]);
        } else {
          setItems(Array.isArray(data) ? data : []);
          setIdx(0);
        }
        setLoading(false); // ★ 修改：無論成功或失敗都結束載入
      });
    return () => {
      mounted = false;
    };
  }, []);

  // 輪播（每 5 秒）；滑鼠移入暫停
  useEffect(() => {
    if (loading) return; // ★ 修改：未載入完成不啟動輪播
    if (!items || items.length <= 1) return;
    const start = () => {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        if (!hoveredRef.current) setIdx((i) => (i + 1) % items.length);
      }, 5000);
    };
    start();
    return () => clearInterval(timerRef.current);
  }, [items, loading]);

  const current = useMemo(
    () =>
      items && items.length ? items[Math.min(idx, items.length - 1)] : null,
    [items, idx]
  );

  const s = loading
    ? { bg: "#f1f5f9", bd: "#e2e8f0", fg: "#64748b" }
    : catStyle(current?.category); // ★ 修改：載入中改為淺灰
  const goDetail = () =>
    !loading && current && navigate(`/education?id=${current.id}`); // ★ 修改：載入中不導頁

  return (
    <div className="news-outer">
      {/* 桌機版：維持原本一行逐條切換 */}
      {!isMobile && (
        <div
          className="news-bar"
          style={{
            ["--news-bg"]: s.bg,
            ["--news-bd"]: s.bd,
            ["--news-fg"]: s.fg,
          }}
          role={loading ? "status" : current ? "button" : "region"} // ★ 修改：無障礙語義
          aria-label={
            loading
              ? "公告讀取中"
              : current
              ? `前往閱讀：${current.title}`
              : "最新消息"
          } // ★ 修改
          onClick={goDetail} // ★ 保留；goDetail 內已避免 loading
          onMouseEnter={() => (hoveredRef.current = true)}
          onMouseLeave={() => (hoveredRef.current = false)}
          title={
            loading ? "公告讀取中…" : current ? "點擊前往閱讀" : "最新消息"
          } // ★ 修改
        >
          <div className="news-label">
            <img src="/icons/idea.png" alt="" className="news-idea-icon" />
            <p className="new-label-content">訊息跑馬燈</p>
          </div>
          <div className="news-window" aria-live="polite">
            {loading ? ( // ★ 修改：載入中文字樣
              <div className="news-line news-empty">公告讀取中…</div>
            ) : current ? (
              <div
                key={`${current.id}-${idx}`}
                className="news-line news-anim-fadeup"
              >
                <span className="news-cat">[{current.category}]</span>
                <span className="news-title" title={current.title}>
                  {current.title}
                </span>
                <span className="news-date">
                  (發布時間 : {fmtDate(current.published_at)})
                </span>
              </div>
            ) : (
              <div className="news-line news-empty">尚無公告</div>
            )}
          </div>
        </div>
      )}

      {/* 手機版：改為水平跑馬燈 */}
      {isMobile && (
        <section className="news-marquee" aria-label="最新消息（跑馬燈）">
          <div className="news-marquee-track">
            {(loading
              ? [
                  {
                    id: "loading",
                    title: "公告讀取中…",
                    category: "",
                    published_at: "",
                  },
                ]
              : items.length
              ? items
              : [
                  {
                    id: "empty",
                    title: "尚無公告",
                    category: "",
                    published_at: "",
                  },
                ]
            ).map((n, i) =>
              // ★ 修改：行動版使用 <Link> 導頁；loading/empty 用 <span>
              n.id && n.id !== "empty" && n.id !== "loading" ? (
                <Link
                  key={`m-${n.id}-${i}`}
                  to={`/education?id=${n.id}`}
                  className="news-pill"
                  title={n.title}
                  style={
                    n.category
                      ? {
                          ["--news-bg"]: catStyle(n.category).bg,
                          ["--news-bd"]: catStyle(n.category).bd,
                          ["--news-fg"]: catStyle(n.category).fg,
                        }
                      : undefined
                  }
                >
                  {n.category ? `[${n.category}] ` : ""}
                  {n.title}
                  {n.published_at ? `（${fmtDate(n.published_at)}）` : ""}
                </Link>
              ) : (
                <span
                  key={`m-${n.id}-${i}`}
                  className="news-pill"
                  title={n.title}
                  style={
                    n.category
                      ? {
                          ["--news-bg"]: catStyle(n.category).bg,
                          ["--news-bd"]: catStyle(n.category).bd,
                          ["--news-fg"]: catStyle(n.category).fg,
                        }
                      : {
                          ["--news-bg"]: "#f1f5f9",
                          ["--news-bd"]: "#e2e8f0",
                          ["--news-fg"]: "#64748b",
                        }
                  }
                >
                  {n.title}
                </span>
              )
            )}

            {!loading &&
              items.map((n, i) =>
                n.id ? (
                  <Link
                    key={`mclone-${n.id}-${i}`}
                    to={`/education?id=${n.id}`}
                    className="news-pill clone"
                    title={n.title}
                    style={{
                      ["--news-bg"]: catStyle(n.category).bg,
                      ["--news-bd"]: catStyle(n.category).bd,
                      ["--news-fg"]: catStyle(n.category).fg,
                    }}
                  >
                    [{n.category}] {n.title}（{fmtDate(n.published_at)}）
                  </Link>
                ) : null
              )}
          </div>
        </section>
      )}
    </div>
  );
}
